import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "LanguageContext";

import PersonalInfoSection from "./components/PersonalInfoSection";
import PhysicalDetailsSection from "./components/PhysicalDetailsSection";
import SubscriptionSection from "./components/SubscriptionSection";
import ActionButtons from "./components/ActionButtons";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import { useDispatch, useSelector } from "react-redux";
import {
  createClientService,
  deleteClientService,
  getClientByIdService,
  getClientsService,
  updateClientService,
} from "redux/services/clients_service";
import { toast } from "react-toastify";

const AddEditClientProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  const { clientInfo, isLoadingAction } = useSelector((state) => state.clients);

  const { language } = useLanguage();

  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isArabic, setIsArabic] = useState(language == "ar" ? true : false);
  const [formData, setFormData] = useState({
    name: isEditMode ? clientInfo?.name : "",
    phone: isEditMode ? clientInfo?.phone : "",
    address: isEditMode ? clientInfo?.address : "",
    height: isEditMode ? clientInfo?.height : "",
    weight: isEditMode ? clientInfo?.weight : "",
    start_date: isEditMode ? clientInfo?.start_date : "",
    end_date: isEditMode ? clientInfo?.end_date : "",
  });
  const [errors, setErrors] = useState({});

  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get("id");

  useEffect(() => {
    if (clientId) {
      setIsEditMode(true);

      dispatch(getClientByIdService(clientId)).then((response) => {
        console.log(response?.payload?.data?.data);
        if (response.payload.status == 200) {
          setFormData(response?.payload?.data?.data);
        }
      });
    }
  }, [location]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Auto-calculate end date based on package type and start date
    if (field === "subscriptionStartDate" || field === "packageType") {
      const startDate =
        field === "subscriptionStartDate"
          ? value
          : formData.subscriptionStartDate;
      const packageType =
        field === "packageType" ? value : formData.packageType;

      if (startDate && packageType) {
        const start = new Date(startDate);
        let endDate = new Date(start);

        switch (packageType) {
          case "monthly":
            endDate.setMonth(endDate.getMonth() + 1);
            break;
          case "quarterly":
            endDate.setMonth(endDate.getMonth() + 3);
            break;
          case "semi-annual":
            endDate.setMonth(endDate.getMonth() + 6);
            break;
          case "annual":
            endDate.setFullYear(endDate.getFullYear() + 1);
            break;
          default:
            break;
        }

        setFormData((prev) => ({
          ...prev,
          subscriptionEndDate: endDate.toISOString().split("T")[0],
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = isArabic
        ? "الاسم الكامل مطلوب"
        : "Full name is required";
    }

    /* if (!formData.age || formData.age < 1 || formData.age > 120) {
      newErrors.age = isArabic ? 'العمر يجب أن يكون بين 1 و 120' : 'Age must be between 1 and 120';
    }*/

    if (!formData.phone.trim()) {
      newErrors.phone = isArabic
        ? "رقم الهاتف مطلوب"
        : "Phone number is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = isArabic ? "العنوان مطلوب" : "Address is required";
    }

    if (!formData.height || formData.height < 50 || formData.height > 300) {
      newErrors.height = isArabic
        ? "الطول يجب أن يكون بين 50 و 300 سم"
        : "Height must be between 50 and 300 cm";
    }

    if (!formData.weight || formData.weight < 20 || formData.weight > 500) {
      newErrors.weight = isArabic
        ? "الوزن يجب أن يكون بين 20 و 500 كجم"
        : "Weight must be between 20 and 500 kg";
    }

    /*  if (!formData.subscriptionStartDate) {
      newErrors.subscriptionStartDate = isArabic ? 'تاريخ بداية الاشتراك مطلوب' : 'Subscription start date is required';
    }*/

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    console.log(formData);
    if (validateForm()) {
      if (isEditMode !== true) {
        dispatch(createClientService(formData))
          .then((response) => {
            console.log(response);
            if (
              response?.payload?.status === 200 ||
              response?.payload?.status === 201
            ) {
              toast.success(
                language === "ar"
                  ? "  تمت عملية الإضافة بنجاح "
                  : "Added successfully !"
              );
              navigate("/client-management");
            } else {
              Object.values(response.payload.errors).forEach((errorArray) => {
                errorArray.forEach((errorMessage) => {
                  toast.warn(errorMessage);
                });
              });
            }
          })
          .catch((error) => {
            console.error(error);
            toast.error("An error occurred");
          })
          .finally(() => {
            //   setLoading(false); // Stop loading
          });
      } else {
        dispatch(updateClientService({ id: clientId, formData: formData }))
          .then((response) => {
            console.log(response);
            if (
              response?.payload?.status === 200 ||
              response?.payload?.status === 201
            ) {
              toast.success(
                language === "ar"
                  ? "  تمت عملية التعديل بنجاح "
                  : "Updated successfully !"
              );
              navigate("/client-management");
            } else {
              Object.values(response.payload.errors).forEach((errorArray) => {
                errorArray.forEach((errorMessage) => {
                  toast.warn(errorMessage);
                });
              });
            }
          })
          .catch((error) => {
            console.error(error);
            toast.error("An error occurred");
          })
          .finally(() => {
            //   setLoading(false); // Stop loading
          });
      }

      // navigate('/client-management');
    }
  };

  const handleCancel = () => {
    navigate("/client-management");
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    dispatch(deleteClientService(clientId)).then((response) => {
      if (
        response.payload?.status === 200 ||
        response.payload?.status === 201 ||
        response.payload?.status === 204
      ) {
        toast.success(
          language === "ar" ? "تم الحذف بنجاح" : "Deleted successfully"
        );
        dispatch(getClientsService({ page: 1 }));
        setShowDeleteModal(false);
        navigate("/client-management");
      }
    });
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${isArabic ? "rtl" : "ltr"}`}
      dir={isArabic ? "rtl" : "ltr"}
    >
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-8">
            <div className="max-w-2xl mx-auto">
              <PersonalInfoSection
                formData={formData}
                errors={errors}
                isArabic={isArabic}
                onInputChange={handleInputChange}
              />

              <PhysicalDetailsSection
                formData={formData}
                errors={errors}
                isArabic={isArabic}
                onInputChange={handleInputChange}
              />

              <SubscriptionSection
                formData={formData}
                errors={errors}
                isArabic={isArabic}
                onInputChange={handleInputChange}
              />

              <ActionButtons
                isEditMode={isEditMode}
                isArabic={isArabic}
                onSave={handleSave}
                onCancel={handleCancel}
                onDelete={handleDelete}
                isLoadingAction={isLoadingAction}
              />
            </div>
          </div>
        </div>
      </main>

      {showDeleteModal && (
        <DeleteConfirmationModal
          isArabic={isArabic}
          clientName={clientInfo?.name}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default AddEditClientProfile;
